import React from 'react';
import { MessagesSquare, Volume2, VolumeX, ChevronDown, Maximize2, Minimize2, Share2, ExternalLink, Info } from 'lucide-react';
import type { Post, VideoDisplayOptions } from '../types';
import VideoPlayer from './VideoPlayer';

interface PostCardProps {
  post: Post;
  isVisible: boolean;
  displayOptions: VideoDisplayOptions;
  onChatClick: () => void;
  isDesktop?: boolean;
}

const VideoMemo = React.memo(VideoPlayer);

export function PostCard({ post, isVisible, displayOptions, onChatClick, isDesktop = false }: PostCardProps) {
  const [isMuted, setIsMuted] = React.useState(displayOptions.isMuted ?? true);
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [showFullCaption, setShowFullCaption] = React.useState(false);
  const [showInfo] = React.useState(true);

  // Update local mute state when displayOptions changes
  React.useEffect(() => {
    if (displayOptions.isMuted !== undefined) {
      setIsMuted(displayOptions.isMuted);
    }
  }, [displayOptions.isMuted]);

  // Auto-play when visible
  React.useEffect(() => {
    if (isVisible) {
      setIsPlaying(true);
    }
  }, [isVisible]); 

  const handleMuteToggle = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    
    // Notify parent component of mute change
    if (displayOptions.onDisplayOptionsChange) {
      displayOptions.onDisplayOptionsChange({
        isMuted: newMutedState
      });
    }
  };

  const handleCenterModeToggle = () => {
    if (displayOptions.onCenterToggle) {
      displayOptions.onCenterToggle();
    }
  };

  return (
    <div className="absolute inset-0 bg-black overflow-hidden">
      <div className={`
        ${displayOptions.centered 
          ? isDesktop 
            ? 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-auto h-[85vh] max-w-[85%] shadow-2xl rounded-lg overflow-hidden' 
            : 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[56.25vh] h-[100vh] max-w-[100vw] max-h-[177.78vw] shadow-2xl' 
          : 'absolute inset-0'}
      `}>
        <VideoMemo
          src={post.videoUrl}
          isVisible={isVisible}
          isPlaying={isPlaying}
          onPlayingChange={setIsPlaying}
          isMuted={isMuted}
          objectFit={displayOptions.centered ? 'contain' : 'cover'}
        />
      </div>
      
      <div className="absolute inset-0 flex items-end pointer-events-none">
        <div className={`w-full p-4 ${isDesktop ? 'pb-8' : 'pb-24'} ${displayOptions.centered ? 'bg-gradient-to-t from-black/90 via-black/40 to-transparent' : 'bg-gradient-to-t from-black/80 via-black/50 to-transparent'} pointer-events-auto`}>
          <div className="flex items-end justify-between max-w-screen-lg mx-auto">
            <div className={`flex-1 ${isDesktop ? 'max-w-md' : ''}`}>
              <div className="relative backdrop-blur-sm bg-black/20 rounded-lg p-3 shadow-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden">
                    <img
                      src={post.userAvatar}
                      alt={post.username}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-white font-semibold text-sm sm:text-base">@{post.username}</p>
                  {isDesktop && (
                    <button className="ml-2 bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1 rounded-full transition-colors">
                      Follow
                    </button>
                  )}
                </div>
                <div className={`relative ${showFullCaption ? '' : 'max-h-[2.5em] overflow-hidden'}`}>
                  <p className="text-white text-xs sm:text-sm">{post.caption}</p>
                  {!showFullCaption && (
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/90" />
                  )}
                </div>
                {showFullCaption && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {post.hashtags.map((tag) => (
                      <span key={tag} className="text-white text-xs sm:text-sm">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setShowFullCaption(!showFullCaption)}
                  className="mt-2 text-gray-400 text-xs flex items-center gap-1 hover:text-white transition-colors backdrop-blur-sm bg-black/30 px-2 py-1 rounded-full shadow-md"
                >
                  {showFullCaption ? 'Less' : 'More'}
                  <ChevronDown className={`w-3 h-3 transition-transform ${showFullCaption ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
            
            <div className="flex flex-col items-center gap-3 sm:gap-4 ml-2 sm:ml-4">
              <button 
                className="bg-black/50 p-2 rounded-full hover:bg-black/70 transition-colors"
                onClick={handleMuteToggle}
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? (
                  <VolumeX className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                ) : (
                  <Volume2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                )}
              </button>
              
              {isDesktop && (
                <button
                  onClick={handleCenterModeToggle}
                  className="bg-black/50 p-2 rounded-full hover:bg-black/70 transition-colors"
                  title={displayOptions.centered ? "Exit cinema mode" : "Enter cinema mode"}
                >
                  {displayOptions.centered ? (
                    <Minimize2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  ) : (
                    <Maximize2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  )}
                </button>
              )}
              
              {isDesktop && (
                <button
                  className="bg-black/50 p-2 rounded-full hover:bg-black/70 transition-colors"
                  title="Share"
                >
                  <Share2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </button>
              )}
              
              <button 
                className="bg-black/50 p-2 rounded-full hover:bg-black/70 transition-colors"
                onClick={onChatClick}
                title="Live chat"
              >
                <MessagesSquare className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </button>
              
              {isDesktop && (
                <button
                  className="bg-black/50 p-2 rounded-full hover:bg-black/70 transition-colors"
                  title="Video information"
                >
                  <Info className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </button>
              )}
              
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white overflow-hidden">
                <img
                  src={post.userAvatar}
                  alt={post.username}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {isDesktop && displayOptions.centered && (
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={() => window.open(post.videoUrl, '_blank')}
            className="bg-black/60 text-white p-2 rounded-full hover:bg-black/80 transition-colors"
            title="Open in new tab"
          >
            <ExternalLink className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}